import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

const ProtectedRoute = ({
  children,
  requireAuth = true,
  requiredPermission = null,
  requiredAnyPermission = null,
  requiredRole = null,
  requiredAnyRole = null,
}) => {
  const { user, loading, hasPermission, hasAnyPermission, hasRole, hasAnyRole } = useAuth()
  const location = useLocation()

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="vite-loading-spinner" style={{ margin: '0 auto 1rem' }}></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    )
  }

  // If route requires authentication and user is not logged in
  if (requireAuth && !user) {
    // Redirect to login page with return url
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // If route is for guests only (like login/register) and user is logged in
  if (!requireAuth && user) {
    // Redirect to dashboard or home page
    return <Navigate to="/" replace />
  }

  // Check permissions if required
  if (requireAuth && user) {
    if (requiredPermission && !hasPermission(requiredPermission)) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-bold text-red-600">Access Denied</h2>
            <p className="mt-2 text-gray-600">You don&apos;t have permission to access this page.</p>
          </div>
        </div>
      )
    }

    if (requiredAnyPermission && !hasAnyPermission(requiredAnyPermission)) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-bold text-red-600">Access Denied</h2>
            <p className="mt-2 text-gray-600">You don&apos;t have permission to access this page.</p>
          </div>
        </div>
      )
    }

    if (requiredRole && !hasRole(requiredRole)) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-bold text-red-600">Access Denied</h2>
            <p className="mt-2 text-gray-600">This page requires {requiredRole} role.</p>
          </div>
        </div>
      )
    }

    if (requiredAnyRole && !hasAnyRole(requiredAnyRole)) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-bold text-red-600">Access Denied</h2>
            <p className="mt-2 text-gray-600">Insufficient role privileges.</p>
          </div>
        </div>
      )
    }
  }

  // User has required authentication state, render children
  return children
}

export default ProtectedRoute